import React, { useMemo } from "react";
import { DayLabel, HourLabel } from "../calendar/components/Labels";
import { useSelectable } from "../calendar/interactivity/selectable";
import { ItemData, ItemId } from "../calendar/utils/types";
import { AVAILABILITY_TYPES, AvailabilityTimeslotData } from "./utils";
import { endOfDay, endOfMonth, endOfWeek, getWeekOfMonth, startOfDay, startOfMonth, startOfWeek } from "../calendar/utils/dates";
import { displayItems, useItems } from "../calendar/utils/items";
import { generateTimeslotKeys, groupSlotKeys, generateGridSlots } from "../calendar/utils/slots";
import { classNames } from "../Availability";
import { useHovering } from "../calendar/interactivity/hovering";
import { useImmer } from "use-immer";

export const generateSlots = (
  slotSize: number,
) => () => {
  const daySlotKeys = generateTimeslotKeys(startOfDay(), endOfDay(), { slotSize });
  const timeGutterSlots = generateGridSlots(daySlotKeys, "time-gutter");
  const dayGridSlots = generateGridSlots(daySlotKeys, "day");

  const weekSlotKeys = generateTimeslotKeys(startOfWeek(), endOfWeek(), { slotSize });
  const weekGridSlots = generateGridSlots(weekSlotKeys, "week");
  const weekDayGroups = groupSlotKeys(weekSlotKeys, (key) => new Date(key).getDay());
  const weekTimeGroups = groupSlotKeys(weekSlotKeys, (key) => key % (24 * 60 * 60 * 1000));
  const dayGutterSlots = generateGridSlots(
    Object.values(weekTimeGroups)[0], 
    "day-gutter"
  );

  const monthSlotKeys = generateTimeslotKeys(
    startOfWeek(startOfMonth()), 
    endOfWeek(endOfMonth()), 
    { slotSize: 1440 }
  );
  const monthGridSlots = generateGridSlots(monthSlotKeys, "month");
  const monthWeekGroups = groupSlotKeys(monthSlotKeys, 
    (key) => getWeekOfMonth(new Date(), new Date(key),));

  return {
    daySlotKeys, timeGutterSlots, dayGridSlots,
    weekSlotKeys, weekGridSlots, weekTimeGroups, dayGutterSlots, weekDayGroups,
    monthSlotKeys, monthGridSlots, monthWeekGroups
  }
}

function itemData(
  data: AvailabilityTimeslotData[],
  type: "daily" | "weekly" | "monthly"
) : ItemData[] {
  return data.filter(item => item.cronData.cronType === type).map(data => ({
    itemId: data.id,
    start: data.cronData.date,
    duration: parseInt(data.timeslot.schedule.duration), // TODO: This should get parsed during props injection
  }));
}

export const SlotGrid: React.FC<{
  slotSize: number;
  data: AvailabilityTimeslotData[],
  addTimeslot: Function,
  ItemContents: React.FC<{itemId: ItemId}>
}> = ({
  slotSize = 60,
  data = [],
  addTimeslot = () => {},
  ItemContents = () => <></>,
}) => {
  const {
    daySlotKeys, timeGutterSlots, dayGridSlots,
    weekSlotKeys, weekGridSlots, weekTimeGroups, weekDayGroups,
    monthSlotKeys, monthGridSlots, dayGutterSlots,
    monthWeekGroups
  } = useMemo(
    generateSlots(slotSize),
    [slotSize]
  );
  
  const { callbacks: hoverCallbacks, isHovered } = useHovering({});

  // Mobile-only: 3-day window (yesterday, today, tomorrow) with wrap-around
  const todayIdx = new Date().getDay();
  const [ startDayIndex, setStartDayIndex ] = useImmer<number>((todayIdx + 6) % 7);
  const visibleDayIdxs = useMemo(() => [0,1,2,3].map((j) => (startDayIndex + j) % 7), [startDayIndex]);
  const [ mobileView, setMobileView ] = useImmer<'weekly' | 'monthly'>('weekly');

  const daily = {
    items: useItems(itemData(data, "daily"), {0: daySlotKeys}, slotSize, 'height', classNames.item),
    ...useSelectable({onSelect: addTimeslot("daily", AVAILABILITY_TYPES.available)})
  }

  const weekly = {
    items: useItems(itemData(data, "weekly"), weekDayGroups, slotSize, 'height', classNames.item),
    ...useSelectable({onSelect: addTimeslot("weekly", AVAILABILITY_TYPES.available)})
  }

  const monthly = {
    items: useItems(itemData(data, "monthly"), monthWeekGroups, 1440, 'width', classNames.item),
    ...useSelectable({onSelect: addTimeslot("monthly", AVAILABILITY_TYPES.available)})
  }

  return  (
    <div className={"select-none h-full max-sm:w-90"}>

      <div className="sm:hidden flex flex-col" style={{minWidth: "max-content"}}>
        {/* Mobile view toggle */}
        <div className="flex items-center justify-center gap-2 pb-2">
          <button
            type="button"
            className={`px-3 py-1 border rounded ${mobileView === 'weekly' ? 'bg-gray-200' : ''}`}
            onClick={() => setMobileView('weekly')}
            aria-pressed={mobileView === 'weekly'}
          >
            Weekly
          </button>
          <button
            type="button"
            className={`px-3 py-1 border rounded ${mobileView === 'monthly' ? 'bg-gray-200' : ''}`}
            onClick={() => setMobileView('monthly')}
            aria-pressed={mobileView === 'monthly'}
          >
            Monthly
          </button>
        </div>
      </div>

      <div className="flex space-x-4 w-full">
        
        <div className={mobileView === 'monthly' ? 'hidden sm:flex' : 'flex'}>
          {/* Time Gutter */}
          <div className="flex-col">
            { /* Mobile: Empty space above weekday columns to keep row alignment */ }
            <div className="sm:hidden mb-5">&nbsp;</div>
            <div className={classNames.slot({isGutter: true})}>Time</div>
            { Object.values(timeGutterSlots).map(({ key, Slot }) => {
              const className = classNames.slot({
                isGutter: true,
                isHovered: isHovered(key, "time"),
              });

              return <Slot
                key={key} 
                className={className} 
                {...hoverCallbacks(key, "time")}
                label={<HourLabel time={key} />}
              />
            }) }
          </div>

          {/*  Day Grid */}
          <div className="flex-col">
            { /* Mobile: Empty space above weekday columns to keep row alignment */ }
            <div className="sm:hidden mb-5">&nbsp;</div>
            <div className={classNames.slot({isGutter: true})}>Daily</div>
            { Object.values(dayGridSlots).map(({ key, Slot }) => {
              const className = classNames.slot({isHovered: isHovered(key, "time"), isSelected: daily.isSelected(key)});
              return (
                <Slot 
                  key={key} 
                  className={className} 
                  {...hoverCallbacks(key, "time")} 
                  {...daily.callbacks(key)}
                >
                  { displayItems(ItemContents, daily.items, key) }
                </Slot>
              )
            }) }
          </div>
        </div>

        <div className="m-l-8 flex-col overflow-x-hidden sm:overflow-x-auto">
          
          {/* Desktop: full grid */}
          <div className="hidden sm:grid grid-cols-7" style={{minWidth: "max-content"}}>
            {/* Day Gutter */}
            { Object.values(dayGutterSlots).map(({ key, Slot }) => {
              const className = classNames.slot({
                isGutter: true,
                isHovered: isHovered(key, "day"),
              });

              return <Slot 
                key={key} 
                className={className} 
                {...hoverCallbacks(key, "day")}
                label={<DayLabel time={key} />}
              />
            }) }

            {/* Week Grid */}
            { Object.values(weekTimeGroups).sort().map((timeGroup) => 
                Object.values(timeGroup).map((key) => {
                  const Slot = weekGridSlots[key]?.Slot || (() => <></>);
                  const className = classNames.slot({isHovered: isHovered(key), isSelected: weekly.isSelected(key)});
                  return <Slot key={key} className={className} {...hoverCallbacks(key)} {...weekly.callbacks(key)}>
                    { displayItems(ItemContents, weekly.items, key) }
                  </Slot>
                })
            )}
          </div>

          {/* Mobile (<sm): Weekly/Monthly toggle and conditional rendering */}
          <div className="sm:hidden flex flex-col" style={{minWidth: "max-content"}}>

            { mobileView === 'weekly' && (
              <>
                {/* Controls above weekday columns only (mobile weekly) */}
                <div className="flex items-center justify-between pb-2">
                  <button type="button" aria-label="Previous day" className="px-2 py-1 border rounded" onClick={() => setStartDayIndex((i) => (i + 6) % 7)}>‹</button>
                  <div className="text-sm text-gray-600">Showing 4 days</div>
                  <button type="button" aria-label="Next day" className="px-2 py-1 border rounded" onClick={() => setStartDayIndex((i) => (i + 1) % 7)}>›</button>
                </div>

                {/* Day Gutter (labels) */}
                <div className="grid grid-cols-4">
                  { visibleDayIdxs.map((dayIdx) => {
                    const entry = Object.values(dayGutterSlots).find(({ key }) => new Date(key).getDay() === dayIdx);
                    if(!entry) { return null; }
                    const { key, Slot } = entry as any;
                    const className = classNames.slot({
                      isGutter: true,
                      isHovered: isHovered(key, "day"),
                    });
                    return <Slot
                      key={`label-${key}`}
                      className={className}
                      {...hoverCallbacks(key, "day")}
                      label={<DayLabel time={key} />}
                    />
                  }) }
                </div>

                {/* Week Grid (4 columns) */}
                <div className="grid grid-cols-4 gap-0">
                  { Object.values(weekTimeGroups).sort().map((timeGroup) => {
                    return visibleDayIdxs.map((dayIdx) => {
                      const key = Object.values(timeGroup).find((k) => new Date(k as number).getDay() === dayIdx) as number | undefined;
                      if(!key) { return null; }
                      const Slot = weekGridSlots[key]?.Slot || (() => <></>);
                      const className = classNames.slot({ isHovered: isHovered(key), isSelected: weekly.isSelected(key) });
                      return <Slot key={key} className={className} {...hoverCallbacks(key)} {...weekly.callbacks(key)}>
                        { displayItems(ItemContents, weekly.items, key) }
                      </Slot>
                    })
                  }) }
                </div>
              </>
            )}

            { mobileView === 'monthly' && (
              <>
                {/* Month labels row (days of week) */}
                <div className="grid grid-cols-7">
                  { Object.values(dayGutterSlots).map(({ key, Slot }) => {
                    const className = classNames.slot({
                      isGutter: true,
                      isHovered: isHovered(key, "day"),
                    });
                    return <Slot 
                      key={`m-label-${key}`}
                      className={className}
                      {...hoverCallbacks(key, "day")}
                      label={<DayLabel time={key} />}
                    />
                  }) }
                </div>
                {/* Month grid (mobile) */}
                <div className="grid grid-cols-7 gap-0">
                  { Object.values(monthGridSlots).map(({ key, Slot }) => {
                    const className = classNames.slot({isHovered: isHovered(key, "day"), isSelected: monthly.isSelected(key)});
                    return <Slot key={key} className={className} {...hoverCallbacks(key, "day")} {...monthly.callbacks(key)}>
                      { displayItems(ItemContents, monthly.items, key) }
                    </Slot>
                  }) }
                </div>
              </>
            )}
          </div>

          <br />

          { /* Month (desktop only) */}
          <div className="hidden sm:grid grid-cols-7 gap-0" style={{minWidth: "max-content"}}>
            {/* Day Gutter */}
            { Object.values(dayGutterSlots).map(({ key, Slot }) => {
              const className = classNames.slot({
                isGutter: true,
                isHovered: isHovered(key, "day"),
              });

              return <Slot 
                key={key} 
                className={className} 
                {...hoverCallbacks(key, "day")}
                label={<DayLabel time={key} />}
              />
            }) }

            {/* Week Grid */}
            { Object.values(monthGridSlots).map(({ key, Slot }) => {
              const className = classNames.slot({isHovered: isHovered(key, "day"), isSelected: monthly.isSelected(key)});
              return <Slot key={key} className={className} {...hoverCallbacks(key, "day")} {...monthly.callbacks(key)}>
                { displayItems(ItemContents, monthly.items, key) }
              </Slot>
            }) }
          </div>
        </div>
      </div>
    </div>
  )
}